import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, Output, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Observable, of, Subscription} from 'rxjs';
import {catchError, last, map, share, startWith, tap} from 'rxjs/operators';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthService } from '@app/core/auth/_services/auth.service';
import { ToastService } from '@app/services/toast.service';
import { FileDto, FileUploadProgressTypes, UploadProgress } from '@app/models/file';
import { PublicFilesService } from '@app/services/public-files.service';
import { StringFormatPipe } from '@app/core/pipes/stringFormat.pipe';


@Component({
  selector: 'app-simple-file-uploader',
  template: `
    <input type="file"
           #fileSelector
           [multiple]="multiple !== undefined"
           hidden
           (change)="onFileUpload($event.target.files); fileSelector.value= null">
    <ng-content></ng-content>
  `,
  styles: [
    `:host {
        display: block;
    }`
  ],
})

export class SimpleFileUploaderComponent implements AfterViewInit {
  private initialized = false;
  @Input() protectedMode: boolean;
  @Output() changes: EventEmitter<FileDto[]> = new EventEmitter();
  @Input() set initialData(files: FileDto[]) {
    if (!files || this.initialized) { return; }
    if (!Array.isArray(files)) { files = [files]; }
    this.rawFiles = files;
    this.initialized = true;
  }

  @Input() allowedExtensions: string ;

  @Input() endpoint: string;

  // @Input() method: string;

  @Input()
  multiple: boolean;
  rawFiles: FileDto[] = [];
  get files(): FileDto[] {
    return this.rawFiles.map(f => ({
      ...f,
      file: null,
    }));
  }

  filesStatus: Array<UploadProgress<FileDto & {tempId: number, remove?: () => void}>> = [];
  private subscriptions: Map<Observable<UploadProgress<FileDto>>, Subscription> = new Map();

  @ViewChild('fileSelector', {static: true})
  private fileSelectorInput: ElementRef<HTMLInputElement>;

  constructor(
    private authService: AuthService,
    private notification: ToastService,
    private fileService: PublicFilesService,
    private translate: TranslateService,
    private changeDetection: ChangeDetectorRef,
    public dialog: MatDialog,
    private stringFormat: StringFormatPipe,
  ) {
  }

  openSelectFile() {
    this.fileSelectorInput.nativeElement.click();
  }

  ngAfterViewInit() {
    if (this.allowedExtensions) {
      this.fileSelectorInput.nativeElement.accept = this.allowedExtensions;
    }
  }

  onFileUpload(files) {
    if (files && files.length > 0) {

      for (const selectedFile of files) {

        // Verify allowed file extension
        if (!this.allowedExtensions?.includes(this.getFileExtension(selectedFile.name))) {
          const msg = this.translate.instant('FILEM.FILE_TYPE_NOT_SUPPORTED');
          this.notification.error(msg);
        } else {
          const reader = new FileReader();
          reader.readAsDataURL(selectedFile);
          reader.onload = () => {
            const newFile: FileDto = ({
              // id: 0,
              name: selectedFile.name,
              file: (reader.result as string).split(',')[1],
            });

            if (!newFile.file || newFile.file.length < 1) {
              this.notification.error(this.translate.instant('FILEM.FILE_EMPTY'));
            } else {
              const tempId = new Date().getTime();
              const newObs = this.postFileApi(newFile);
              let subscription: Subscription;
              const fileStatusObj = {
                ...newFile,
                tempId,
                remove: null,
                file: selectedFile,
              };

              if (this.multiple !== undefined) {
                this.filesStatus.push({
                  status: FileUploadProgressTypes.LOADING,
                  data: fileStatusObj,
                });
              } else {
                this.subscriptions.forEach(e => e.unsubscribe());
                this.filesStatus = [{
                  status: FileUploadProgressTypes.LOADING,
                  data: fileStatusObj,
                }];
              }

              subscription = newObs
                .pipe(
                  tap(file => {
                    const updateFile = this.filesStatus.find(e => e.data.tempId === tempId);
                    Object.assign(updateFile, { progress: file.progress });
                  }),
                  last(),
                )
                .subscribe(fileResult => {
                  this.subscriptions.delete(newObs);
                  if(!fileResult.data.errorMessages?.length){
                    const newFileResult = {
                      ...newFile,
                      id: fileResult.data.id,
                      file: selectedFile,
                      url: fileResult.data.url,
                      fileLength: fileResult.data.fileLength,
                      mimeType: fileResult.data.mimeType,
                    };
                    if (this.multiple === undefined) {
                      this.rawFiles = [newFileResult];
                    } else {
                      this.rawFiles.push(newFileResult);
                    }
                    this.changes.emit(this.rawFiles);
                    this.notification.success(this.translate.instant('FILE-MANAGER.TABLE.ATT-SUCCES'));
                  }else{
                    // console.log(fileResult.data.errorMessages);
                    let errorsArray = [];
                    
                    for(let i = 0; i < fileResult.data.errorMessages.length; i++){
                      let msg = "";
                      if(fileResult.data.errorMessages[i].includes('#')){
                        // console.log(fileResult.data.errorMessages[i].split('#'));
                        let newMsg = fileResult.data.errorMessages[i].split('#');
                        if(newMsg[0].includes('|')){
                          let formateMsg = newMsg[0].split('|');
                          msg = this.stringFormat.transform((this.translate.instant('errors' + formateMsg[0]) + newMsg[1]), formateMsg[1])
                        }else{
                          msg = this.translate.instant('errors' + newMsg[0]) + newMsg[1];
                        }
                      }else{
                        msg = this.translate.instant('errors' + fileResult.data.errorMessages[i]);
                      }
                      errorsArray.push(msg);
                    }
                    const dialogRef = this.dialog.open(ErrorMessagesModal, {
                      width: '400px',
                      data: errorsArray,
                    });
                    // this.notification.error(this.translate.instant('ALERTS.IMPORT_FILE_ERRORS.ColumnName Entidad del Alerta does not exists.'));
                  }
                });
              fileStatusObj.remove = () => {
                subscription.unsubscribe();
              };
              subscription.add(() => {
                this.subscriptions.delete(newObs);
                this.filesStatus = this.filesStatus.filter(x => x.data.tempId !== tempId);
              });

              this.subscriptions.set(newObs, subscription);

            }

          };
        }
      }
    } else {
      // this.notification.display(this.translate.instant('FILEM.NOFILE'));
    }
  }

  // getFileById(id, fileName) {
  //   if (id > 0) {
  //     this.spinnerDisplay = true;
  //     const apiRequest = !this.dueDiligenceMode ?
  //     this.fileService.downloadFile(id, this.answerId) :
  //     this.fileService.downloadDueDiligenceRequestAnswerFile(id, this.answerId);

  //     apiRequest.subscribe(res => {
  //       this.downloadFile(res, fileName, res.type);
  //       this.spinnerDisplay = false;
  //     },
  //     (err) => {/*this._toast.error(err);*/ this.spinnerDisplay = false; })
  //   } else {
  //     // this._toast.success(this._translateService.instant('FILEM.FILE-EXIST'));
  //   }
  // }

  // downloadFile(data: any, fileName: string, fileType: string) {
  //   const blob = new Blob([data], { type: fileType });
  //   saveAs(blob, fileName);
  // }

  private postFileApi(file: FileDto): Observable<UploadProgress<FileDto>> {
    return this.fileService.postFileWithProgress(file, this.endpoint).pipe(
      startWith({status: FileUploadProgressTypes.LOADING} as UploadProgress<FileDto>),
      map((response) => {
          switch (response.status) {
            case FileUploadProgressTypes.FINISHED:
              file = {
                ...response.data,
                ...file,
              };
          }
          this.changeDetection.markForCheck();
          return {
            ...response,
            data: file,
          };
        
      }),
      catchError(err => {
        this.notification.error(this.translate.instant('FILEM.ERROR'));
        return of({
          status: FileUploadProgressTypes.ERROR,
        });
      }),
      share(),
    );
  }

  // FileSize Format Func
  formatBytes(bytes) {
    const decimals = 2;
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  removeFile(file: FileDto) {
    this.rawFiles = this.rawFiles.filter(x => {
      return x.id ? x.id !== file.id : x.url !== file.url;
    });
    this.changes.emit(this.rawFiles);
  }

  // removeLoadingFile(file: Observable<FileDto>) {
  //   this.subscriptions.get(file).unsubscribe();
  //   this.subscriptions.delete(file);
  //   this.filesStatus = this.filesStatus.filter(obs => obs !== file);
  // }

  private getFileExtension(fileName: string): string {
    const fileParts = fileName.split('.');
    return fileParts[fileParts.length - 1];
  }

  private getFileName(fileName: string): string {
    const fileParts = fileName.split('.');
    return fileParts[0];
  }

  downloadFile(file: FileDto) {
    this.fileService.downloadFileTemplate(file.id).subscribe();
  }
}

//ErrorMessagesModal
@Component({
  selector: 'dialog-overview-example-dialog',
  template: `<div>
                <h1 style="text-align: center">{{warning}}</h1>
                <div *ngFor="let error of data">
                    <p>-{{error}}</p>
                </div>
             </div>`

})
export class ErrorMessagesModal {

  warning = '';

  constructor(
    public dialogRef: MatDialogRef<ErrorMessagesModal>,
    @Inject(MAT_DIALOG_DATA) public data,
    public dialog: MatDialog,
    private translate: TranslateService,
  ) {
      this.warning = this.translate.instant("ALERT_DIALOG.TITLE");
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
